import type { IBond } from "@/modules/bond/common/types";
import { useGetBondsQuery } from "@/modules/bond/services";
import { setShowBalances } from "@/modules/dashboard/state";
import type { IError } from "@/shared/common/types";
import { PageWidgetTemplate } from "@/shared/components";
import { FinanceItem } from "@/shared/components/FinanceItem";
import { useAppDispatch, useShowBalance } from "@/shared/hooks";
import classNames from "classnames";
import { type Dispatch, type SetStateAction, useEffect } from "react";

interface Props {
  limit?: number;
  isDetailOpen?: boolean;
  selectedBond?: IBond | null;
  setSelectedBond?: Dispatch<SetStateAction<IBond | null>>;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
}

export const Bonds = ({
  limit,
  isDetailOpen,
  selectedBond,
  setSelectedBond,
  setIsOpen,
}: Props) => {
  const dispatch = useAppDispatch();
  const { handleShowBalances } = useShowBalance();
  const {
    data: bonds,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetBondsQuery({
    pageSize: limit,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const onShowDetails = (bond: IBond) => {
    setSelectedBond?.(bond);
    if (!isDetailOpen) {
      setIsOpen?.(true);
    }
  };

  useEffect(() => {
    if (bonds?.length) {
      const showBalances = Array(bonds?.length).fill(false);
      dispatch(setShowBalances(showBalances));
    }
  }, [bonds]);

  return (
    <PageWidgetTemplate
      isFetching={isFetching}
      isError={isError}
      error={error as IError}
      refetch={refetch}
      isEmpty={!bonds?.length}
    >
      {bonds?.map((bond, i) => (
        <div key={bond.contractNumber} onClick={() => onShowDetails(bond)}>
          <FinanceItem
            {...bond}
            index={i}
            handleShowBalances={handleShowBalances}
            className={classNames({ "selected": selectedBond?.id === bond.id })}
          />
        </div>
      ))}
    </PageWidgetTemplate>
  );
};
